<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable vue/require-component-is -->
<template>
  <div
    v-if="liquid?.siteId"
    ref="page"
    class="main-collection critical-component-clear"
  >
    <transition name="fade">
      <empty-search
        v-if="isSearchPage && !isLoading && !finalProducts.length"
        :query="query"
      />
    </transition>

    <div
      class="main-collection__header-container"
      js-sticky-header="container"
    >
      <div
        v-if="liquid.breadcrumbs?.plp"
        class="main-collection__breadcrumbs"
      >
        <breadcrumbs
          :breadcrumb-list="liquid.breadcrumbs.breadcrumbList"
        />
      </div>

      <div
        v-if="!isSearchPage || isLoading || finalProducts.length"
        class="main-collection__header"
        :class="{
          'has-visual-text-facets-enabled': liquid.displayVisualTextFacet === true,
          'has-description': pageDescription,
          'has-visual-facets': visualFacet,
          'only-text-facets': !visualFacet && hasTextFacet,
          'active-facets': activeFacetValues?.length,
        }"
      >
        <div class="main-collection__title-container">
          <h1
            class="main-collection__title font-family-freight"
            v-html="pageTitle"
          />
        </div>

        <div
          v-if="pageDescription"
          class="
            main-collection__description
            text-body-2xs-regular
            text-body-xs-regular-desktop
          "
          v-html="pageDescription"
        />

        <visual-facet
          v-if="!liquid.specialtyTemplate && finalFacets?.length && visualFacet && !hideFilters"
          class="main-collection__visual-facets"
          :facet="visualFacet"
          :is-loading="isLoading"
          :alignment="liquid.displayVisualTextFacet === true ? 'left': null "
          @facet-value-changed="handleFacetValueChanged"
        />

        <div
          v-if="(hasTextFacet && finalFacets?.length && !hideFilters) ||
           liquid.enableAdditionalToggles"
          class="main-collection__text-facets-container"
        >
          <text-facet
            class="main-collection__text-facets"
            :facet="textFacet || liquid.textFacet"
            :is-loading="isLoading"
            :enable-additional-toggles="liquid.enableAdditionalToggles || false"
            @facet-value-changed="handleFacetValueChanged"
          />
        </div>

        <div class="main-collection__options-container">
          <facet-drawer-trigger
            v-if="!liquid.specialtyTemplate && finalFacets?.length && !hideFilters"
            class="main-collection__facet-trigger"
          />

          <button
            v-if="!liquid.specialtyTemplate"
            class="main-collection__mobile-toggle"
            type="button"
            :aria-label="$string('collection.toggle_layout')"
            @click="handleLayoutToggle"
          >
            <span
              class="main-collection__layout-icon main-collection__layout-one"
              :class="{ [$class('active')]: mobileLayout === 'one' }"
            >
              <icon-layout-one />
            </span>

            <span
              class="main-collection__layout-icon main-collection__layout-two"
              :class="{ [$class('active')]: mobileLayout === 'two' }"
            >
              <icon-layout-two />
            </span>
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="activeFacetValues?.length"
      class="main-collection__sub-header"
    >
      <active-facets
        v-if="searchspringResults"
        :active-facet-values="activeFacetValues"
        :is-loading="isLoading"
        @facet-value-changed="handleFacetValueChanged"
        @facets-cleared="handleFacetsCleared"
      />
    </div>

    <facet-drawer
      v-if="loadFacetDrawer && finalFacets?.length"
      :facets="finalFacets"
      :is-loading="isLoading"
      :num-products="finalProducts?.length || liquid.resultsPerPage"
      :sorting-options="searchspringResults?.sorting?.options || []"
      :selected-sort="selectedSortingOption"
      @facets-changed="handleFacetsChanged"
      @sort-changed="handleSortChanged"
    />

    <div
      v-if="isLoading && !finalProducts.length"
      class="main-collection__products"
      :class="[
        { [$class('loading')]: isLoading },
        liquid.specialtyTemplate ? 'main-collection__specialty-grid' : 'grid'
      ]"
    >
      <div
        v-for="(product, index) of loadingProducts"
        :key="`${product?.id}-${index}` || `product-card-${index}`"
        class="main-collection__item"
        :class="[gridColClass, $class('active')]"
        :data-index="index"
        :dy-sku="product?.sku"
        :style="{ transitionDelay: `${index * 0.05}s` }"
      >
        <div
          class="product-card product-card--small"
          :class="$class('loading')"
        >
          <div class="product-card__image-container">
            <div><span class="images product-card__primary-image" /></div>
          </div>
          <div class="product-card__footer">
            <ul class="product-card__badges" />
            <div class="product-card__footer-content">
              <p class="product-card__title" />
            </div>
            <div class="product-card__specialty-badges" />
          </div>
        </div>
      </div>
    </div>
    <div
      v-else-if="finalProducts.length"
      class="main-collection__products"
      :class="[
        { [$class('loading')]: isLoading },
        liquid.specialtyTemplate ? 'main-collection__specialty-grid' : 'grid'
      ]"
    >
      <template
        v-for="(product, index) of finalProducts"
        :key="`${product?.id}-${index}` || `product-card-${index}`"
      >
        <in-grid-banner
          v-if="getBanner(index) && !activeFacetValues?.length"
          class="main-collection__item"
          :class="{
            [gridColClass]: getBanner(index)?.type === 'product'
          }"
          :banner="getBanner(index)"
          :dy-sku="product?.sku"
          :lazyload="getBannerLazyLoad(index)"
          :specialty-template="liquid.specialtyTemplate"
          :style="{ transitionDelay: `${index * 0.05}s` }"
        />

        <div
          class="main-collection__item"
          :class="[gridColClass, $class('active')]"
          :dy-sku="product?.sku"
          :data-index="index"
          :style="{ transitionDelay: `${index * 0.05}s` }"
        >
          <product-card
            is-collection-card
            :searchspring-product="product"
            :card-index="index"
            :card-layout="gridCardLayout"
            :carousel-limit="liquid.carouselLimit"
            enable-image-carousel
            get-shopify-product
            heading-element="h2"
            :is-animatable="numQueriesPerformed < 2"
            :show-wishlist="!liquid.specialtyTemplate"
            :product-card-location="{
              key: 'productListing',
            }"
            show-updated-success
            :show-quick-add="showAddToCart"
          />
        </div>
      </template>

      <template v-if="!activeFacetValues?.length">
        <in-grid-banner
          v-for="(banner, bannerIndex) of endGridBanners"
          :key="`content-banner-${bannerIndex}`"
          class="main-collection__item"
          :class="{
            [gridColClass]: banner.type === 'product'
          }"
          :banner="banner"
          :dy-sku="product?.sku"
          :specialty-template="liquid.specialtyTemplate"
          :style="{ transitionDelay: `${(finalProducts.length + bannerIndex) * 0.05}s` }"
        />
      </template>
    </div>

    <button
      v-if="showBackToTop"
      class="main-collection__back-to-top"
      type="button"
      @click="scrollToTop"
    >
      <icon-arrow-up />

      <span
        class="text-body-2xs-regular"
        v-text="$string('collection.back_to_top')"
      />
    </button>

    <searchspring-pagination
      v-if="searchspringResults?.pagination?.totalPages > 1"
      :pagination="searchspringResults?.pagination"
      :is-loading="isLoading"
      :url="currentUrl"
      @page-changed="handlePageChanged"
    />

    <div
      v-if="liquid.seo?.title || liquid.seo?.text"
      class="main-collection__seo-container"
    >
      <div class="container main-collection__seo">
        <h2
          v-if="liquid.seo.title"
          class="main-collection__seo-title text-body-2xs-regular"
          v-html="liquid.seo.title"
        />

        <transition-height>
          <div
            v-if="liquid.seo.text"
            class="text-body-2xs-regular"
          >
            <span
              class="main-collection__seo-text text-body-2xs-regular"
              :class="{ 'truncate': truncateText && isTruncated }"
              v-html="liquid.seo.text"
            />

            <button
              v-if="truncateText"
              type="button"
              class="main-collection__seo-toggle"
              @click="() => { isTruncated = !isTruncated }"
              v-text="truncateToggleText"
            />
          </div>
        </transition-height>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main collection (main-collection)
 * -----------------------------------------------------------------------------
 * Main collection page component.
 *
 * @param {Object} liquid - Formatted Liquid object, including facet data.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'

import ProductCard from '~global/product-card/product-card'
import TextFacet from '~async/searchspring/text-facet/text-facet'
import TransitionHeight from '~async/transition-height/transition-height'
import VisualFacet from '~async/searchspring/visual-facet/visual-facet'

import IconArrowUp from '~icons/directional-navigation/arrow/up.svg'
import IconLayoutOne from '~icons/general/layout-one.svg'
import IconLayoutTwo from '~icons/general/layout-two.svg'

import { formatFacet, formatSearchspringProduct, searchspringApiCall } from '~/helpers/searchspring'
import localizeUrl from '~/core/helpers/localize-url'
import InGridBanner from '~async/in-grid-banner/in-grid-banner'

export default {
  name: 'MainCollection',

  components: {
    ActiveFacets: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.active-facets' */
        '~async/searchspring/active-facets/active-facets'
      ),
    }),
    EmptySearch: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.empty-search' */
        '~async/empty-search/empty-search'
      ),
    }),
    FacetDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.facet-drawer' */
        '~async/searchspring/facet-drawer/facet-drawer'
      ),
    }),
    FacetDrawerTrigger: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.facet-drawer-trigger' */
        '~async/searchspring/facet-drawer-trigger/facet-drawer-trigger'
      ),
    }),
    Breadcrumbs: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.breadcrumbs' */
        '~async/breadcrumbs/breadcrumbs'
      ),
    }),
    IconArrowUp,
    IconLayoutOne,
    IconLayoutTwo,
    InGridBanner,
    ProductCard,
    SearchspringPagination: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.searchspring-pagination' */
        '~async/searchspring/searchspring-pagination/searchspring-pagination'
      ),
    }),
    TextFacet,
    TransitionHeight,
    VisualFacet,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        cardLayout: null,
        carouselLimit: 4,
        description: null,
        handle: null,
        inGridBanners: null,
        resultsPerPage: 24,
        seo: null,
        siteId: null,
        specialtyTemplate: null,
        textFacet: null,
        title: null,
        visualFacet: null,
        displayVisualTextFacet: false,
      }),
    },
  },

  data() {
    return {
      fullFacets: [],
      activeFacetValues: [],
      finalFacets: [],
      finalProducts: [],
      inGridBanners: [
        ...this.liquid?.inGridBanners?.contentBanners || null,
        ...this.liquid?.inGridBanners?.productBlocks || null,
        ...this.liquid?.inGridBanners?.hotspots || null,
      ],
      isFacetDrawerLoaded: false,
      // Whether search is being performed
      isLoading: true,
      isReady: false,
      // SEO description truncate state
      isSearchPage: this.$variable('page.type') === 'search',
      isTruncated: true,
      loadFacetDrawer: false,
      loadingProducts: Array.apply(false, {
        length: this.liquid?.resultsPerPage || 12,
      }),
      mobileLayout: 'two',
      numQueriesPerformed: 0,
      page: 1,
      pageType: this.$variable('page.type'),
      query: null,
      // The list of facets selected by the user
      selectedFacets: {},
      selectedSortingOption: null,
      // The filters passed to SS
      searchspringFilters: [],
      // The latest results from SS
      searchspringResults: null,
      // The latest product results from Shopify
      showBackToTop: false,
      templateSuffix: this.$variable('template.suffix'),
      initialFacets: [],
      enableLocalizedFilters: window.cnvs?.settings?.collection?.enableLocalizedFilters,
      dyAddToCartObserver: false,
      showAddToCart: true,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * The current URL for the collection
     * @returns {String} The current URL
     */
    currentUrl() {
      const urlParams = []

      this.searchspringFilters.forEach((filter) => {
        urlParams.push(
          filter.split('=')
            .map((part) => encodeURIComponent(part))
            .join('='),
        )
      })

      if (this.page > 1) {
        urlParams.push(`page=${this.page}`)
      }

      let url = `${cnvs.routes.collection}/${this.liquid?.handle || ''}`

      if (urlParams.length) {
        url = `${url}?${urlParams.join('&')}`
      }

      return url
    },

    /**
     * Compute hide facets if locale is not en
     * @returns {Boolean}
     */
    hideFilters() {
      const locale = window.cnvs.request.locale.shop_locale.locale
      return locale !== 'en' && !this.enableLocalizedFilters
    },

    /**
     * Compute if collection has text facet.
     * @returns {Boolean}
     */
    hasTextFacet() {
      return (this.textFacet || this.liquid.textFacet) && !this.liquid.specialtyTemplate
    },

    /**
     * Computes class name for grid column
     * @returns {String}
     */
    gridColClass() {
      if (this.liquid.specialtyTemplate) {
        return 'col'
      }

      return `col ${this.mobileLayout === 'two' ? 'xs12' : 'xs-span'} m12 l16`
    },

    /**
     * Computes layout for product card in grid
     * @returns {String}
     */
    gridCardLayout() {
      if (this.liquid.specialtyTemplate) {
        return 'large'
      }

      if (this.mobileLayout === 'two') {
        return 'small'
      }

      return this.liquid.cardLayout
    },

    /**
     * Display banner at the end of the product grid if banner position is
     * greater than product cards on the page
     * @returns {Array}
     */
    endGridBanners() {
      const bannerPos = this.isMobile ? 'positionMobile' : 'positionDesktop'

      return this.inGridBanners?.filter(banner => banner[bannerPos] >=
        (this.finalProducts?.length || this.liquid.resultsPerPage)) || []
    },

    /**
     * Has in grid banners currently in the DOM
     * @return {Boolean}
     */
    hasVisibleInGridBanners() {
      if (this.endGridBanners?.length) {
        return true
      }

      if (!this.finalProducts?.length || this.activeFacetValues?.length) {
        return false
      }

      return Boolean(this.finalProducts.find((prod, index) => {
        return this.getBanner(index)
      }))
    },

    /**
     * Compute page description.
     * @returns {String}
     */
    pageDescription() {
      if (this.isSearchPage) {
        return ''
      }

      return this?.liquid.description?.replaceAll('<p', '<p class="text-body-2xs-regular text-body-xs-regular-desktop"')
    },

    /**
     * Compute page title.
     * @returns {String}
     */
    pageTitle() {
      if (this.isSearchPage) {
        return this.$string('search.title', { replace: { query: this.query } })
      }

      return this.liquid.title
    },

    /**
     * Handles read more/less text
     * @returns {String}
     */
    truncateToggleText() {
      if (this.isTruncated) {
        return this.$string('collection.read_more')
      }

      return this.$string('collection.read_less')
    },

    /**
     * Handles SEO text truncation based on description length
     * @returns {Boolean}
     */
    truncateText() {
      return this.liquid.seo?.text?.length > 100
    },

    /**
     * The text facet, with settings in Shopify
     * @returns {Object} Facet
     */
    textFacet() {
      return this.getLiquidFacet(this.liquid?.textFacet)
    },

    /**
     * The visual facet, with settings in Shopify
     * @returns {Object} Facet
     */
    visualFacet() {
      return this.getLiquidFacet(this.liquid?.visualFacet)
    },
  },

  watch: {

    /**
     * Watch final products to fade them in.
     */
    finalProducts: {
      async handler() {
        await this.$nextTick()

        if (this.isSearchPage && !this.isLoading && !this.finalProducts.length) {
          this.setZeroResultPage(true)
        } else {
          this.setZeroResultPage(false)
        }
      },
      deep: true,
    },

    /**
     * Watch is loading to emit update.
     */
    isLoading: {
      handler() {
        cnvs.EventBus.emit('plp:is-loading-update', this.isLoading)
      },
      immediate: true,
    },

    /**
     * Watch text facet to emit update.
     */
    textFacet: {
      handler() {
        cnvs.EventBus.emit('plp:text-facet-value-changed', this.textFacet)
      },
      deep: true,
      immediate: true,
    },
  },

  async created() {
    this.performSearchspringSearch(true)
    await this.$nextTick()
    this.setDataFromUrl()

    // delayed searchspring call to consistenly load the filters
    // when reloading the page
    setTimeout(() => {
      this.performSearchspringSearch(true)
    }, this.$timing('skeleton'))

    // For the first open request, trigger lazy loading
    // for the drawer. It will then handle opening itself.
    cnvs.EventBus.listen('overlay:open-facet-drawer', (target = false) => {
      this.loadFacetDrawer = true
      this.loadStylesheet('facet-drawer')

      if (target && this.isMobile) {
        this.setOpenAccordion(target)
      }
    })
  },

  mounted() {
    this.getGridLayout()
    this.handleBackToTopDisplay()
    this.observeShowAddToCartDY()

    cnvs.EventBus.listen('plp-header:text-facet-value-changed', this.handleFacetValueChanged)
    this.setReferrerData()

    cnvs.EventBus.listen('plp:layout-toggle', this.handleLayoutToggle)

    /**
     * Attemtps to prevent page crash
     * on pinch zoom - mobile
     */
    let isZoomed = false
    document.addEventListener('touchmove', (event) => {
      if (!this.isMobile) {
        return
      }
      if (event.scale > 1.05) {
        document.body.classList.add('disable-elements')
        isZoomed = true
      }
    })

    document.addEventListener('gestureend', (event) => {
      if (!this.isMobile) {
        return
      }

      if (event.scale <= 1) {
        if (isZoomed) {
          document.body.classList.remove('disable-elements')
          isZoomed = false
        }
      }
    })
  },

  methods: {

    /**
     * Sets open active state for mobile accordion facet
     * @param {String} target - The target facet to open
     */
    async setOpenAccordion(target) {
      await this.$nextTick()
      const triggerTimeout = setTimeout(() => {
        document.querySelectorAll('.accordion__trigger').forEach((trigger) => {
          if (trigger.getAttribute('data-for')?.indexOf(target) > -1 &&
            !trigger.classList.contains('is-active')) {
            trigger.click()
          }
        })
        clearTimeout(triggerTimeout)
      }, 300)
    },

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      loadStylesheet: 'components/loadStylesheet',
      setZeroResultPage: 'search/setZeroResults',
    }),

    /**
     * Handles banner display based on index position
     * @param {Number} position - Index of grid
     * @returns {Boolean|Object}
     */
    getBanner(position) {
      const currentUrl = new URL(window.location)
      const params = new URLSearchParams(currentUrl.search)
      const page = params.get('page') ? Number(params.get('page')) : 1

      const bannerPos = this.isMobile ? 'positionMobile' : 'positionDesktop'
      const inGridBanner = this.inGridBanners.find(
        banner => banner[bannerPos] === position && (!banner.page || banner.page === page),
      )

      if (!inGridBanner) {
        return false
      }

      return inGridBanner
    },

    /**
     * Gets whether a banner should lazy load
     * @param {Number} index - Banner index
     * @returns {Boolean} Lazy load
     */
    getBannerLazyLoad(index) {
      if (this.liquid?.visualFacet?.values?.length) {
        return this.isMobile ? index > 1 : index > 3
      }

      return this.isMobile ? index > 3 : index > 7
    },

    /**
     * Gets a formatted facet with liquid and ss data
     * @param {Object} liquidFacet - Liquid facet data
     */
    getLiquidFacet(liquidFacet) {
      if (!liquidFacet || !this.finalFacets) {
        return null
      }

      // formatted facet values based on active facets
      const fullFacets = this.fullFacets.map(facet => ({
        ...facet,
        values: facet.values.map(value => ({
          ...value,
          active: this.activeFacetValues.some(activeFacet => activeFacet.field === facet.field &&
            activeFacet.value === value.value),
        })),
      }))

      const formattedFacet = formatFacet({
        ssFacet: fullFacets?.find((ssF) => ssF.field === liquidFacet.field) || null,
        ssSummaryFacet: this.searchspringResults?.filterSummary || null,
        liquidFacet,
      })

      // Preserve the order chosen in Shopify settings
      const sortedValues = liquidFacet.values.reduce((vals, val) => {
        const formattedVal = formattedFacet?.values?.find((fVal) => fVal.value === val.field)
        if (formattedVal) {
          vals.push(formattedVal)
        }
        return vals
      }, [])

      if (!sortedValues || !sortedValues?.length) {
        return null
      }

      return {
        ...formattedFacet,
        values: sortedValues,
      }
    },

    /**
     * Sets SS and Shopify data in the component when SS updates.
     * @param {Object} results - The SS query results
     */
    searchspringUpdated(results, setFacets = false) {
      this.isReady = true
      this.searchspringResults = results?.searchspring
      this.numQueriesPerformed++

      this.finalProducts = results?.searchspring?.results?.map(
        (res) => formatSearchspringProduct(res),
      ) || []

      /**
       * The SS facets, with:
       * - Liquid label
       * - The values have the latest active status
       *
       * Omits facets that have
       * a visual facet in Shopify which is set to replace the SS one
       * a text facet in Shopify which is set to replace the SS one
       */
      if (setFacets) {
        this.initialFacets = results?.searchspring?.facets
      }

      // restore facet values when no products found for facet combinations
      if (!this.fullFacets || this.fullFacets?.length === 0) {
        this.fullFacets = results?.searchspring?.facets
      }

      const finalFacets = this.initialFacets?.reduce((facets, ssFacet) => {
        let liquidFacet

        if (this.liquid?.visualFacet?.field === ssFacet.field) {
          liquidFacet = this.liquid.visualFacet
        }

        if (!liquidFacet && this.liquid?.textFacet?.field === ssFacet.field) {
          liquidFacet = this.liquid.textFacet
        }

        const finalFacet = formatFacet({
          ssFacet,
          ssSummaryFacets: this.searchspringResults?.filterSummary,
          liquidFacet,
        })

        // Hidden when the facet has a visual/text facet set to replace(
        const isHidden = (
          ssFacet?.field === this.liquid?.visualFacet?.field &&
          this.liquid?.visualFacet?.isReplacement
        ) || (
          ssFacet?.field === this.liquid?.textFacet?.field && this.liquid?.textFacet?.isReplacement
        )

        facets.push({
          ...finalFacet,
          isHidden,
        })

        return facets
      }, [])

      // Set the active facet values based on ss response summary
      const activeFacetValues = results?.searchspring?.filterSummary?.map((filter) => {
        // Set facet value in finalFacets if missing, since it's active
        const fctIndex = finalFacets.findIndex((fct) => fct.field === filter.field)
        const fctValueIndex = fctIndex === -1
          ? -1
          : finalFacets[fctIndex].values.findIndex((val) => val.value === filter.value)

        // If the facet exists but this active value is missing, add it
        // Otherwise make sure it's set to active
        if (fctIndex > -1) {
          if (fctValueIndex > -1) {
            finalFacets[fctIndex].values[fctValueIndex].active = true
          }
        }

        // Create active facet data
        const fctValue = fctValueIndex === -1 ? {} : finalFacets[fctIndex].values[fctValueIndex]
        const facetType = results?.searchspring?.facets?.find(
          (fct) => fct.field === filter.field,
        )?.type

        return {
          ...filter,
          ...fctValue || {},
          type: facetType ? facetType : null,
          active: true,
        }
      }) || []

      this.finalFacets = finalFacets
      this.activeFacetValues = activeFacetValues

      // set active attribute for facets
      this.finalFacets.forEach(finalFacet => {
        finalFacet.values.forEach(finalValue => {
          const isActive = finalValue.active
          this.fullFacets.filter(
            facet => facet.field === finalFacet.field,
          ).forEach(matchingFacet => {
            matchingFacet.values.filter(value => value.value === finalValue.value)
              .forEach(matchingValue => {
                if (isActive || matchingValue.active) {
                  matchingValue.active = true
                }
              })
          })
        })
      })

      // when invalid facet combinations are selected,
      // restore facet values
      if (this.finalFacets.length === 0) {
        this.finalFacets = this.fullFacets.map(facet => ({
          ...facet,
          values: facet.values.map(value => ({
            ...value,
            active: this.activeFacetValues.some(activeFacet => activeFacet.field === facet.field &&
              activeFacet.value === value.value),
          })),
        }))
      }

      // Load stylesheets needed based on results
      if (activeFacetValues.length) {
        this.loadStylesheet('active-facets')
      }

      if (finalFacets?.length) {
        if (!this.liquid?.specialtyTemplate) {
          this.loadStylesheet('facet-drawer-trigger')
        }
      }

      if (results?.searchspring?.pagination?.totalPages > 1) {
        this.loadStylesheet('searchspring-pagination')
      }

      // Set the selected sorting option based on ss response active sorting opt
      this.selectedSortingOption = results?.searchspring?.sorting?.options?.find(
        (opt) => opt.active === 1,
      )

      // Set the selected facet values based on ss response facets or summary
      if (this.finalFacets.length) {
        this.resetSelectedFacets(this.finalFacets)
      } else {
        this.setSelectedFacetsUsingSummary(this.activeFacetValues)
      }

      if (this.finalProducts.length === 0 && this.activeFacetValues.length > 0) {
        // identify the last selected filter
        const lastSelected = this.activeFacetValues[this.activeFacetValues.length - 1]
        if (lastSelected) {
          // remove last selected filter from active facets
          this.activeFacetValues = this.activeFacetValues.filter(
            facet => !(facet.field === lastSelected.field && facet.value === lastSelected.value),
          )

          // remove from selectedfacets
          if (this.selectedFacets[lastSelected.field]) {
            this.selectedFacets[lastSelected.field][lastSelected.value] = false
          }

          // update searchspring filters to update url params
          this.searchspringFilters = this.searchspringFilters.filter(
            filter => !filter.includes(`filter.${lastSelected.field}=${encodeURIComponent(lastSelected.value)}`),
          )

          this.finalFacets = this.fullFacets.map(facet => ({
            ...facet,
            values: facet.values.map(value => ({
              ...value,
              active: this.activeFacetValues.some(
                activeFacet => activeFacet.field === facet.field &&
                  activeFacet.value === value.value,
              ),
            })),
          }))
          this.performSearchspringSearch(true)
        }
      }
      // If search page, redirect if appropriate. Otherwise update URL params
      if (this.isSearchPage) {
        // Set SS redirect url.
        const redirect = results?.searchspring?.merchandising?.redirect || ''

        if (redirect) {
          window.location = localizeUrl(redirect)
          return
        }

        if (!this.finalProducts?.length) {
          this.loadStylesheet('empty-search')
        }
      } else {
        history.replaceState({
          html: '',
        }, '', this.currentUrl)
      }

      this.isLoading = false
    },

    /**
     * Sets the SS filter data, triggering a SS search API call
     * to get product data.
     */
    search() {
      if (!this.isLoading) {
        this.isLoading = true
        const filters = []

        // Build filter params
        Object.entries(this.selectedFacets).forEach(([facet, facetVals]) => {
          Object.entries(facetVals).forEach(([facetVal, isActive]) => {
            if (!isActive) {
              return
            }
            filters.push(`filter.${facet}=${encodeURIComponent(facetVal)}`)
          })
        })

        if (this.selectedSortingOption) {
          filters.push(`sort.${this.selectedSortingOption.field}=${this.selectedSortingOption.direction}`)
        }
        this.searchspringFilters = filters
        this.performSearchspringSearch(true)
      }
    },

    /**
     * Resets the selected facets object to match the ones passed in.
     */
    resetSelectedFacets(newFinalFacets) {
      const selectedFacets = {}
      newFinalFacets.forEach((facet) => {
        selectedFacets[facet.field] = {}
        facet.values.forEach((facetValue) => {
          selectedFacets[facet.field][facetValue.value] = facetValue.active
        })
      })
      this.selectedFacets = selectedFacets
    },

    /**
     * Resets the selected facets object to match the SS filter summary values.
     * Used when there are no results for a selected combination of facets.
     */
    setSelectedFacetsUsingSummary(activeFacetsValues) {
      const selectedFacets = {}
      activeFacetsValues.forEach((facetValue) => {
        if (!selectedFacets[facetValue.field]) {
          selectedFacets[facetValue.field] = {}
        }
        selectedFacets[facetValue.field][facetValue.value] = true
      })
      this.selectedFacets = selectedFacets
    },

    /**
     * Sets active filters based on values found in URL search parameters
     */
    setDataFromUrl() {
      const params = new URLSearchParams(location.search)
      const strings = []
      let page = 1

      params.forEach((value, key) => {
        if (key === 'page') {
          page = value
          return
        }

        if (key === 'q') {
          if (this.isSearchPage) {
            this.query = value
          }
          return
        }

        strings.push(`${key}=${value}`)
      })

      this.searchspringFilters = strings
      this.page = page
    },

    /**
     * Handles a facet value changed event from a child component
     * @param {String} field - The facet field name
     * @param {String} value - The facet value that changed
     * @param {Boolean} selected - The facet value's new selection
     */
    handleFacetValueChanged({
      field,
      value,
      selected,
    }) {
      if (!this.selectedFacets[field]) {
        this.selectedFacets[field] = {}
      }

      this.selectedFacets[field][value] = selected
      this.page = 1
      this.search()
    },

    /**
     * Handles an update of multiple facets at once.
     * @param {Object} selections - The facets and their selected values,
     *  as well as the sorting option
     */
    handleFacetsChanged(selections) {
      const newSelectedFacets = {
        ...this.selectedFacets,
      }

      Object.entries(selections.facets).forEach(([key, val]) => {
        if (newSelectedFacets[key]) {
          newSelectedFacets[key] = {
            ...newSelectedFacets[key],
            ...val,
          }
        } else {
          newSelectedFacets[key] = val
        }
      })

      this.selectedFacets = newSelectedFacets
      this.selectedSortingOption = selections.sort
      this.page = 1
      this.search()
    },

    /**
     * Handles a sort option update.
     * @param {Object} sortOption - The sort option
     */
    handleSortChanged(sortOption) {
      this.selectedSortingOption = sortOption
      this.page = 1
    },

    /**
     * Handles an event to clear all selected facets.
     */
    handleFacetsCleared() {
      window.history.replaceState({}, '', window.location.origin + window.location.pathname)
      this.query = null
      this.finalFacets = []
      this.fullFacets = []
      this.activeFacetValues = []
      this.selectedSortingOption = null
      this.searchspringFilters = []
      this.selectedFacets = {}
      this.page = 1
      this.loadFacetDrawer = false
      this.search()
    },

    /**
     * Handles a page change event.
     * @param {Number} page - The page number
     */
    handlePageChanged(page) {
      this.page = page
      this.search()

      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        navigator.userAgent.includes(['iPhone'])
        ? 'auto'
        : 'smooth'

      setTimeout(() => {
        scrollTo({
          behavior,
          left: 0,
          top: 0,
        })
      }, 200)
    },

    /**
     * Sets mobile grid layout from session storage
     */
    getGridLayout() {
      const mobileLayout = localStorage.getItem('grid-layout')

      if (mobileLayout) {
        this.mobileLayout = mobileLayout
      }
    },

    /**
     * Handles product grid layout toggle
     */
    handleLayoutToggle() {
      this.mobileLayout = this.mobileLayout === 'one' ? 'two' : 'one'
      cnvs.EventBus.emit('plp:layout-updated', this.mobileLayout)
      localStorage.setItem('grid-layout', this.mobileLayout)
    },

    /**
     * Handles back to top button display based on page height
     * @returns {Boolean}
     */
    handleBackToTopDisplay() {
      const page = this.$refs.page

      if (!page) {
        return
      }

      const resize = new ResizeObserver(entries => {
        for (const entry of entries) {
          const element = entry.contentRect

          this.showBackToTop = element.top > 0
            ? element.height + element.top > window.innerHeight
            : element.height > window.innerHeight
        }
      })

      resize.observe(page)
    },

    /**
     * Scrolls to top of page
     */
    scrollToTop() {
      const focusEl = document.querySelector('#main-content')
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth'

      scrollTo({
        behavior,
        left: 0,
        top: 0,
      })

      focusEl.focus()
    },

    /**
     * Sets PLP referrer data for PDPs that need it
     */
    setReferrerData() {
      if (!this.liquid?.title || !this.liquid?.handle) {
        return
      }

      sessionStorage.setItem(`referrer-plp-${this.liquid.handle}`, JSON.stringify({
        title: this.liquid.title,
        url: `${cnvs.routes.collection}/${this.liquid.handle}`,
      }))
    },

    /**
     * Fetches results from SS based on selected options
     */
    async performSearchspringSearch(setFacets = false) {
      const data = await searchspringApiCall({
        collectionHandle: this.liquid.handle,
        filters: this.searchspringFilters,
        page: this.page,
        query: this.query || '',
        redirectResponse: this.query ? 'direct' : '',
        resultsPerPage: this.liquid.resultsPerPage,
        siteId: this.liquid.siteId,
      })

      this.searchspringUpdated(data, setFacets)
    },

    /**
     * Checks for DY element in the DOM and triggers
     * hiding product card atc button
     */
    observeShowAddToCartDY() {
      const observerConfig = {
        attributes: true,
        childList: true,
      }

      this.dyAddToCartObserver = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type !== 'childList' || !mutation.addedNodes.length) {
            continue
          }

          const triggerElement = document.querySelector('.dy-plp-show-atc')

          if (triggerElement) {
            this.showAddToCart = false

            this.dyAddToCartObserver.disconnect()
            return
          }
        }
      })

      this.dyAddToCartObserver.observe(document.body, observerConfig)
    },
  },
}
</script>

<style lang="scss">
@import './main-collection';
</style>
